import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Minus } from "lucide-react";

export const WATER_ITEMS = new Set(["Zagori 1lt", "Avra Mineral Water (500ml)", "Sparkling Water Xinonero"]);
export const classify = (rules, category, group, name) => ({
  coffee: (rules?.coffee || ["Coffee"]).includes(category),
  glasses: (rules?.glasses || []).includes(category) || WATER_ITEMS.has(name),
});

const Choice = ({ label, active, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "bg-slate-900 text-white" : "bg-sand-100 text-slate-600 ring-1 ring-sand-300"}`}>{label}</button>
);

export const ItemOptionsDialog = ({ ctx, rules, onAdd, onClose }) => {
  const open = !!ctx;
  const item = ctx?.item;
  const { coffee, glasses } = ctx ? classify(rules, ctx.category, ctx.group, item.name) : {};
  const [sugar, setSugar] = useState(null);
  const [type, setType] = useState(null);
  const [milk, setMilk] = useState(null);
  const [glassCount, setGlassCount] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");

  useEffect(() => {
    setSugar(null); setType(null); setMilk(null); setGlassCount(""); setQty(1); setNote("");
  }, [ctx?.item?.name]);

  const coffeeReady = !coffee || (sugar && type && milk);

  const confirm = () => {
    const options = [];
    if (coffee) options.push(sugar, type, milk);
    if (glasses && Number(glassCount) > 0) options.push(`${glassCount} glass${Number(glassCount) > 1 ? "es" : ""}`);
    onAdd({ name: item.name, price: item.price, station: ctx.station, qty, options: options.length ? options : null, note: note.trim() });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="item-options-dialog">
        {item && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl tracking-wide">{item.name}</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">{item.price.toFixed(2)}€{item.desc ? ` · ${item.desc}` : ""}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {coffee && (
                <>
                  <div>
                    <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Sugar <span className="text-destructive">*</span></p>
                    <div className="flex flex-wrap gap-2">
                      {["No sugar", "Medium sweet", "Sweet"].map((s) => <Choice key={s} label={s} active={sugar === s} onClick={() => setSugar(s)} testid={`opt-sugar-${s}`} />)}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Type <span className="text-destructive">*</span></p>
                    <div className="flex gap-2">
                      {["Normal", "Decaf"].map((s) => <Choice key={s} label={s} active={type === s} onClick={() => setType(s)} testid={`opt-type-${s}`} />)}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Milk <span className="text-destructive">*</span></p>
                    <div className="flex gap-2">
                      {["With milk", "No milk"].map((s) => <Choice key={s} label={s} active={milk === s} onClick={() => setMilk(s)} testid={`opt-milk-${s}`} />)}
                    </div>
                  </div>
                </>
              )}
              {glasses && (
                <div>
                  <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">How many glasses?</p>
                  <input type="number" min="0" data-testid="opt-glasses" value={glassCount} onChange={(e) => setGlassCount(e.target.value)}
                    placeholder="e.g. 2" className="h-10 w-28 rounded-lg border border-sand-300 bg-white px-3 text-sm outline-none focus:border-brand" />
                </div>
              )}
              <div>
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Note</p>
                <input data-testid="opt-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. no ice, extra hot"
                  className="h-10 w-full rounded-lg border border-sand-300 bg-white px-3 text-sm outline-none focus:border-brand" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 rounded-full bg-sand-100 p-1">
                  <button type="button" data-testid="opt-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-700"><Minus size={15} /></button>
                  <span className="w-6 text-center font-bold">{qty}</span>
                  <button type="button" data-testid="opt-qty-plus" onClick={() => setQty((q) => q + 1)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-700"><Plus size={15} /></button>
                </div>
                <button data-testid="opt-add" onClick={confirm} disabled={!coffeeReady}
                  className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50">
                  Add · {(item.price * qty).toFixed(2)}€
                </button>
              </div>
              {coffee && !coffeeReady && <p className="text-center text-[11px] font-semibold text-destructive">Please choose sugar, type and milk</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
